import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ChevronDown } from "lucide-react";
import { UserCircle } from "@phosphor-icons/react";
import { LEAD_STATUSES } from "./LeadCells";
import { StatusPill } from "../lux/Primitives";
import { LEAD_STATUS_TONE } from "./leadActions";

const STATUS_TONE = {
  new: "text-blue-300", contacted: "text-emerald-300", quoted: "text-primary",
  won: "text-emerald-400", lost: "text-red-400",
};

const OPEN_STATUSES = new Set(["new", "contacted", "quoted"]);

// SALES-034 scope 1: ONE rule for the colour of the assigned text. An OPEN lead
// (new/contacted/quoted) with no rep shows "Unassigned" in the warning amber
// already used for "No reply yet" on the same row; a won/lost lead with no rep
// is muted; a lead with a rep shows the name in the foreground colour. Used by
// the desktop cell (scope 2) and the phone/tablet cards (scope 3).
// SALES-036: a lead texted to B squad and still inside its window shows who has
// first claim and how long is left, instead of "Unassigned". Null otherwise.
export function claimWindow(lead) {
  const offer = lead.claim_offer || {};
  if (offer.status !== "open" || (lead.salesperson || "").trim()) return null;
  const until = Date.parse(offer.expires_at || "");
  const mins = Math.ceil((until - Date.now()) / 60000);
  if (!(mins > 0)) return null;
  const at = new Date(until).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
  return { label: `B squad ${mins}m`, title: `B squad has first claim until ${at}` };
}

// SALES-036: the owner's tooltip says when a lead was claimed by text.
function ownerTitle(lead, rep) {
  const o = lead.claim_offer || {};
  if (!rep || o.claimed_via !== "text" || !o.claimed_at) return rep;
  const at = new Date(o.claimed_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return `${rep}, claimed by text ${at}`;
}

export function assignedTextClass(lead) {
  if ((lead.salesperson || "").trim()) return "text-foreground";
  return OPEN_STATUSES.has((lead.status || "new").toLowerCase())
    ? "text-amber-400" : "text-muted-foreground";
}

// SALES-034 scope 3: the rep's fixed spot on a card — a decorative UserCircle
// icon (the same icon LeadMatchDialog draws a lead's owner with) then the rep's
// name (or "Unassigned", coloured by scope 1), medium weight, at the same size
// as the line below it, truncating with a full-name title. Screen readers hear
// the aria-label, not the icon.
export function CardAssignedLine({ lead, testid }) {
  const rep = (lead.salesperson || "").trim();
  const win = claimWindow(lead);
  return (
    <span
      className={`flex items-center justify-end gap-1.5 min-w-0 max-w-full text-xs font-medium ${win ? "text-primary" : assignedTextClass(lead)}`}
      data-testid={testid} title={win ? win.title : ownerTitle(lead, rep) || "Unassigned"}
      aria-label={win ? win.title : rep ? `Assigned to ${rep}` : "Unassigned"}
    >
      <UserCircle size={14} className="shrink-0" aria-hidden="true" />
      <span className="truncate">{win ? win.label : rep || "Unassigned"}</span>
    </span>
  );
}

// SALES-031 / SALES-026: at rest a cell is a static button styled like the
// app's Select trigger — no <Select> rendered. It becomes a real dropdown only
// while THIS one cell is being edited, so the page never holds more than one
// row dropdown and typing in the dialog re-renders no <Select>s.
export function AssignedInlineCell({ lead, team, editing, onStartEdit, onCancelEdit, onCommit }) {
  const current = (lead.salesperson || "").trim();
  const name = lead.customer_name || "this lead";
  const win = claimWindow(lead);
  if (!editing) {
    return (
      <button
        type="button" onClick={onStartEdit} title={win ? win.title : ownerTitle(lead, current) || "Unassigned"}
        aria-label={`Assigned salesperson for ${name} — click to change`}
        data-testid={`lead-assign-${lead.id}`}
        className="group flex w-full items-center gap-1 rounded-md py-0.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <span className="truncate underline-offset-2 decoration-muted-foreground group-hover:underline group-focus-visible:underline">{current || (win ? <span className="text-xs text-primary" data-testid={`lead-claim-window-${lead.id}`}>{win.label}</span> : <span className={assignedTextClass(lead)}>Unassigned</span>)}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/55 transition-colors group-hover:text-muted-foreground group-focus-visible:text-muted-foreground" />
      </button>
    );
  }
  const options = current && !team.some((n) => n.toLowerCase() === current.toLowerCase()) ? [current, ...team] : team;
  return (
    <Select defaultOpen value={current || "__none"} onValueChange={(v) => onCommit(v === "__none" ? "" : v)} onOpenChange={(o) => { if (!o) onCancelEdit(); }}>
      <SelectTrigger aria-label={`Assigned salesperson for ${name}`} data-testid={`lead-assign-${lead.id}-select`} className="h-9 w-full bg-input border-border/60 text-xs rounded-xl focus-visible:ring-2 focus-visible:ring-primary/60">
        <SelectValue placeholder="Unassigned" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none">— Unassigned —</SelectItem>
        {options.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function StatusInlineCell({ lead, editing, onStartEdit, onCancelEdit, onCommit }) {
  const status = (lead.status || "new").toLowerCase();
  const name = lead.customer_name || "this lead";
  if (!editing) {
    return (
      <button
        type="button" onClick={onStartEdit}
        aria-label={`Status ${status} for ${name} — click to change`}
        data-testid={`lead-status-${lead.id}`}
        className="group inline-flex items-center gap-1 rounded-full text-left transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <StatusPill status={status} tone={LEAD_STATUS_TONE[status]} />
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground/55 transition-colors group-hover:text-muted-foreground group-focus-visible:text-muted-foreground" />
      </button>
    );
  }
  return (
    <Select defaultOpen value={status} onValueChange={onCommit} onOpenChange={(o) => { if (!o) onCancelEdit(); }}>
      <SelectTrigger aria-label={`Status for ${name}`} data-testid={`lead-status-${lead.id}-select`} className={`h-9 w-full bg-input border-border/60 label-mono rounded-xl focus-visible:ring-2 focus-visible:ring-primary/60 ${STATUS_TONE[status] || ""}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}</SelectContent>
    </Select>
  );
}
